import AssignSpeakerModal from '@src/components/sidepanel/modals/AssignSpeakerModal';
import SpeakerFormModal from '@src/components/transcript/SpeakerFormModal';
import { useSpeakerManagementService } from '@src/hooks/useSpeakerManagementService';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import React, { useEffect, useState } from 'react';

import { NESTED_IN_MODAL_Z } from '../../common/topLayerZ';

import type { SpeakerWithColor } from '@src/services/SpeakerManagementService';

const logger = LogManager.getLogger('feature:AssignTranscriptSpeaker');

/**
 * Links an unassigned transcript speaker ("Speaker 2") to a Dato speaker from
 * inside the reframe modal (PLAT-11992): pick one from the story or the
 * showcase, or create one — the Speakers panel's flow, nested in the modal.
 */
export function AssignTranscriptSpeaker({
  speakerLabel,
  speakerName,
  onAssigned,
  onClose,
}: {
  speakerLabel: string;
  speakerName: string;
  /** The transcript speaker now maps to `datoSpeakerId`. */
  onAssigned: (datoSpeakerId: string) => void;
  onClose: () => void;
}) {
  const videoCreator = useVideoCreatorStore();
  const storyId = videoCreator.story?.id;
  const albumId = videoCreator.story?.primaryShowcase?.id;
  const speakerService = useSpeakerManagementService();
  const [mode, setMode] = useState<'pick' | 'create'>('pick');
  const [speakers, setSpeakers] = useState<{
    story: SpeakerWithColor[];
    showcase: SpeakerWithColor[];
    mappings: Record<string, string>;
  }>({ story: [], showcase: [], mappings: {} });

  useEffect(() => {
    if (!storyId) return;
    let cancelled = false;
    speakerService
      .fetchSpeakersForStory(storyId)
      .then(result => {
        if (cancelled || !result.ok) return;
        setSpeakers({
          story: result.value.storySpeakers,
          showcase: result.value.orgSpeakers,
          mappings: result.value.mappings,
        });
      })
      .catch(error =>
        logger.warn('Failed to load speakers', extractErrorInfo(error)),
      );
    return () => {
      cancelled = true;
    };
  }, [storyId, speakerService]);

  if (!storyId) return null;

  if (mode === 'create') {
    return (
      <SpeakerFormModal
        isOpen
        zIndex={NESTED_IN_MODAL_Z}
        onClose={onClose}
        onSubmit={async data => {
          const created = await speakerService.createAndAssignSpeaker(
            storyId,
            albumId,
            speakerLabel,
            data,
            speakers.mappings,
          );
          if (!created.ok) return false;
          onAssigned(created.value.id);
          return true;
        }}
      />
    );
  }

  return (
    <AssignSpeakerModal
      isOpen
      zIndex={NESTED_IN_MODAL_Z}
      onClose={onClose}
      storySpeakers={speakers.story}
      organizationSpeakers={speakers.showcase}
      speakerLabel={speakerLabel}
      speakerDisplayName={speakerName}
      onCreateNewSpeaker={() => setMode('create')}
      onAssign={async datoSpeakerId => {
        const assigned = await speakerService.assignSpeaker(
          storyId,
          speakerLabel,
          datoSpeakerId,
        );
        if (!assigned.ok) return false;
        onAssigned(datoSpeakerId);
        return true;
      }}
    />
  );
}
